import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Mixpanel } from "lib/mixpanel/mixpanel";

import { MIXPANEL_TOKEN } from "lib/constants";
import { useUserData } from "lib/store/user";

import { formatMoney, sortBy } from "./utils";
import { SortIcon } from "../icons/sortIcon";
import type { HeadingsType } from "./types";
import type {
  BriefingReportPageData,
  StartupTableEntry,
} from "content-ui/briefings/fetchers/briefingReportFetcher";

/**
 * Returns true if string contains only digits (presence of decimal point returns false)
 * @param {string} value
 * @returns boolean
 */
function isNum(value: string): boolean {
  return /^\d+$/.test(value);
}

/**
 * Turns values into numbers - returns 0 if figure invalid
 * @param entry
 * @returns number
 */
function processNumericEntry(entry: string | number | null): number {
  if (typeof entry === "number") return entry;
  if (typeof entry === "string" && isNum(entry)) {
    return Number(entry);
  }
  return 0;
}

export default function Table({
  briefing,
}: {
  briefing: BriefingReportPageData;
}) {
  const { userId } = useUserData();

  const sanitiseData = (data: StartupTableEntry[]) => {
    return data.map((entry: StartupTableEntry) => {
      entry.founded = processNumericEntry(entry.founded);
      entry.valuation = processNumericEntry(entry.valuation);
      entry.lastRoundSize = processNumericEntry(entry.lastRoundSize);
      entry.totalFunding = processNumericEntry(entry.totalFunding);
      return entry;
    });
  };

  const tableEntries = sanitiseData(briefing.tableItem);

  const [activeSort, setActiveSort] = useState("name");
  const [filter, setFilter] = useState("All");
  const [tableData, setTableData] = useState([
    ...sortBy(activeSort, tableEntries, true),
  ]);

  const mixpanelRef = useRef<Mixpanel | null>(null);

  useEffect(() => {
    mixpanelRef.current = new Mixpanel(MIXPANEL_TOKEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @ts-ignore
  const [sortState, setSortState] =
    // eslint-disable-next-line no-unused-vars
    useState<{ [key in HeadingsType]: boolean }>({
      name: false,
      location: false,
      founded: false,
      "last round size": false,
      "total funding": false,
      valuation: false,
      stage: false,
    });

  const headings: HeadingsType[] = [
    "name",
    "location",
    "founded",
    "stage",
    "total funding",
    "last round size",
    "valuation",
  ];

  const countryData = useMemo(() => {
    const count = tableEntries.reduce((obj, next) => {
      const country = next?.country;
      if (!country) return obj;

      if (!obj[country]) {
        obj[country] = 1;
      } else {
        obj[country] = obj[country] += 1;
      }
      return obj;
    }, {});

    const topFilterOptions = Object.entries(count)
      .sort((a, b) => {
        if (!a[1] || !b[1]) return 0;
        return a[1] < b[1] ? 1 : -1;
      })
      .slice(0, 5)
      .map((el) => el[0]);

    return {
      count,
      list: ["All", ...Array.from(topFilterOptions)],
    };
  }, [tableEntries]);

  const handleFilter = (country: string) => {
    if (userId && mixpanelRef.current) {
      mixpanelRef.current.track("Briefing Table Filtering", {
        "Filtered By": country,
        distinct_id: userId,
        "Briefing title": briefing.name,
      });
    }

    if (country === "All") {
      setTableData([...tableEntries]);
      setFilter("All");
      return;
    }
    const filteredData = tableEntries.filter((el) => el.country === country);
    setTableData([...filteredData]);
    setFilter(country);
  };

  const Filter = () => {
    return (
      <div className="mt-[22px] flex flex-wrap text-[#72706f]">
        {countryData.list.map((country: string) => (
          <button
            key={country}
            className={classNames("mb-[3px] mr-6 pt-[4px]", {
              "border-b-2 border-cerise font-[600] text-cerise":
                filter === country,
            })}
            onClick={() => handleFilter(country)}
          >
            <span className="flex items-center">
              {country}{" "}
              <span
                className={classNames(
                  "ml-[6px] rounded-lg px-[5px] text-[9px] leading-tight",
                  {
                    "bg-[rgba(233,237,245,0.5)]": filter !== country,
                    "bg-[rgba(218,20,94,0.04)]": filter === country,
                  }
                )}
              >
                {country === "All"
                  ? tableEntries.length
                  : countryData.count[country]}
              </span>
            </span>
          </button>
        ))}
      </div>
    );
  };

  const handleSort = (heading: string) => {
    if (userId && mixpanelRef.current) {
      mixpanelRef.current.track("Briefing Table Sorting", {
        "Sorted By": heading,
        distinct_id: userId,
        "Briefing title": briefing.name,
      });
    }

    setActiveSort(heading);
    setTableData([...sortBy(heading, tableData, sortState[heading])]);
    const newSortState = sortState;
    newSortState[heading] = !sortState[heading];
    setSortState(newSortState);
  };

  const Headings = () => (
    <>
      {headings.map((heading) => {
        return (
          <button
            key={heading}
            className={classNames(
              "sticky top-0 z-10 flex border-b-[1px] border-[#e9edf5] bg-[rgba(247,249,252,0.95)] py-[5px] pr-4 text-[12px] uppercase",
              {
                "pl-2": heading === "location",
                "sticky left-0 z-[11] border-r-[1px] pl-2": heading === "name",
              }
            )}
            onClick={() => handleSort(heading)}
          >
            <span
              className={`${
                heading === activeSort && "font-[600]"
              } mr-2 flex items-center whitespace-nowrap text-[#72706f]`}
            >
              {heading}
              <span className="ml-[3px] inline-block">
                {activeSort === heading ? (
                  <SortIcon
                    colour={"#72706f"}
                    activeColour={"#DC085C"}
                    isAsc={sortState[activeSort]}
                  />
                ) : (
                  <SortIcon colour={"#72706f"} />
                )}
              </span>
            </span>
          </button>
        );
      })}
    </>
  );

  const Data = () => (
    <>
      {tableData.map((company, i) => {
        const cellClasses =
          "py-[10px] pr-4 border-b border-[#e9edf5] flex items-center text-[14px] md:text-[16px]";
        const cellRightClasses = `${cellClasses} text-right`;

        const lastRoundSize = formatMoney(company?.lastRoundSize);
        const totalFunding = formatMoney(company?.totalFunding);
        const valuation = formatMoney(company?.valuation);

        return (
          <Fragment key={i}>
            <div
              className={`${cellClasses} sticky left-0 border-r-[1px] border-[#e9edf5] bg-[rgba(255,255,255,0.97)]`}
            >
              <div className="flex flex-col justify-center pl-2 md:whitespace-nowrap">
                <span className="font-[600] text-cerise">
                  {company?.link ? (
                    <a
                      href={company.link}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        if (userId && mixpanelRef.current) {
                          mixpanelRef.current.track("Link Click", {
                            "Link Clicked": company.link,
                            Component: "Startups Table",
                            distinct_id: userId,
                          });
                        }
                      }}
                    >
                      {company?.name}
                    </a>
                  ) : (
                    <p>{company?.name}</p>
                  )}
                </span>
                <p className="hidden text-[15px] font-[600] md:block">
                  {company?.descriptionLineOne}
                </p>
                <p className="hidden text-[15px] md:block">
                  {company?.descriptionLineTwo}
                </p>
              </div>
            </div>
            <div className={`${cellClasses} whitespace-nowrap pl-2`}>
              {company?.city}
              {company?.city && company?.country && ", "}
              {company?.country}
            </div>
            <div className={`${cellRightClasses}`}>{company?.founded}</div>
            <div className={`${cellRightClasses} pr-8`}>
              {company?.lastRoundStage ? (
                <span className="flex items-center whitespace-nowrap rounded-sm bg-[#e1fcef] px-[4px] text-[12px] leading-snug text-[#14804A]">
                  <span className="inline-block">
                    <div className="mr-[5px] h-[5px] w-[5px] rounded-sm bg-[#14804A]"></div>
                  </span>{" "}
                  {company?.lastRoundStage}
                </span>
              ) : (
                <p>-</p>
              )}
            </div>
            <div className={cellRightClasses}>
              {/* @ts-ignore */}
              {totalFunding && totalFunding[0] > 0 ? (
                <p>
                  €{totalFunding[0]}
                  {totalFunding[1]}
                </p>
              ) : (
                <p>-</p>
              )}
            </div>
            <div className={cellRightClasses}>
              {/* @ts-ignore */}
              {lastRoundSize && lastRoundSize[0] > 0 ? (
                <p>
                  €{lastRoundSize[0]}
                  {lastRoundSize[1]}
                </p>
              ) : (
                <p>-</p>
              )}
            </div>

            <div className={cellRightClasses}>
              {/* @ts-ignore */}
              {valuation && valuation[0] > 0 ? (
                <p>
                  €{valuation[0]}
                  {valuation[1]}
                </p>
              ) : (
                <p>-</p>
              )}
            </div>
          </Fragment>
        );
      })}
    </>
  );

  if (briefing.tableItem.length === 0) return null;
  return (
    <div className="rounded-sm text-[16px]">
      <Filter />
      <div className="mt-6 max-w-full overflow-auto text-left">
        <div className="inline-block max-h-[70vh] text-[#72706f]">
          <div className="grid grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr] md:grid-cols-[repeat(7,1fr)]">
            <Headings />
            <Data />
          </div>
        </div>
      </div>
    </div>
  );
}
